import { ENUM__html_ref_stub } from '@/enums/ENUM__html_ref_stub';
import { use_effect } from '@/hooks/_base/use_effect';
import { use_ref_with_fallback } from '@/hooks/_base/use_ref_with_fallback';
import { use_debounced_function } from '@/hooks/debounce/use_debounced_function';
import { use_slice_generation } from '@/hooks/store/use_slices';
import { HOOK_GRID2D_CELL__size } from '@/pages/_base/grid2d_cell/_hooks/HOOK_GRID2D_CELL__size';
import { assert_instanceof } from '@shared/asserts/utils_asserts';
import { DTO__grid2d } from '@shared/dto/grid2d/DTO__grid2d';
import { DTO__grid2d_generation } from '@shared/dto/grid2d/DTO__grid2d_generation';
import { DTO__grid2d_virtual } from '@shared/dto/grid2d_virtual/DTO__grid2d_virtual';
import { EShortcutType } from '@shared/dto/hint_shortcuts/e_shortcut_type';
import { useMemo, useState } from 'react';
import { GRID2D_CELL__generation } from '../../grid2d_cell/views/GRID2D_CELL__generation';
import { WATCHER_GRID2D_GALLERY_GENERATION__cell_current_set } from '../../grid2d_gallery_generation/WATCHER_GRID2D_GALLERY_GENERATION__cell_current_set';
import { ViewShortcutTrigger } from '../../shortcut_hint/ViewShortcutTrigger';
import { WATCHER_GRID2D__change_cell_size_by_scroll } from '../../watchers/grid2d/WATCHER_GRID2D__change_cell_size_by_scroll';
import { WatcherImageToTextTranslate } from '../../watchers/image_to_text/WatcherImageToTextTranslate';
import { GRID2D_BARS_bottom } from '../bars/GRID2D_BARS_bottom';

interface PROPS {
  grid2d: DTO__grid2d;
}

export function GRID2D__fixed_generation_same_images({ grid2d }: PROPS) {
  /*
   * STORE
   */

  const { generation_current } = use_slice_generation(['generation_current']);

  /*
   * STATE
   */

  const ref_container = use_ref_with_fallback(ENUM__html_ref_stub.DIV);
  const ref_grid = use_ref_with_fallback(ENUM__html_ref_stub.DIV);

  const [grid_size_px, set_grid_size_px] = useState({ width: 0, height: 0 });

  const { cell_width, cell_height } = HOOK_GRID2D_CELL__size(generation_current);

  const grid2d_virtual = useMemo(() => DTO__grid2d_virtual.CREATE_STUB(), []);

  const update_grid_size_px_debounced = use_debounced_function(update_grid_size_px, 1300);

  /*
   * EFFECTS
   */

  use_effect(() => {
    const observer = new ResizeObserver(() => update_grid_size_px_debounced());

    observer.observe(ref_container.current);

    return () => observer.disconnect();
  }, []);

  use_effect(() => {
    window.resize_on(update_grid_size_px_debounced);

    return () => window.resize_off(update_grid_size_px_debounced);
  }, [update_grid_size_px_debounced]);

  /*
   * EVENTS
   */

  /*
   * METHODS
   */

  function update_grid_size_px() {
    const { clientWidth: width, clientHeight: height } = ref_container.current;

    if (width !== grid_size_px.width || height !== grid_size_px.height) {
      set_grid_size_px({ width, height });
    }
  }

  /*
   * RENDERS
   */

  function render_cell() {
    assert_instanceof(grid2d, DTO__grid2d_generation);

    return (
      <GRID2D_CELL__generation
        on_mouse_leave_cell={() => {}}
        on_mouse_enter_cell={() => {}}
        x={0}
        y={0}
        //
        {...{ cell_width, cell_height }}
        //
        grid2d_generation_current={grid2d}
      />
    );
  }

  /*
   * HTML
   */

  if (generation_current.is_stub()) return null;
  if (grid2d.is_stub()) return null;

  assert_instanceof(grid2d, DTO__grid2d_generation);

  return (
    <>
      <WatcherImageToTextTranslate {...{ grid2d }} entity={generation_current} />
      <WATCHER_GRID2D__change_cell_size_by_scroll {...{ div_grid: ref_container.current }} />

      <WATCHER_GRID2D_GALLERY_GENERATION__cell_current_set {...{ grid2d }} />

      <ViewShortcutTrigger hint_shortcut_keys={[EShortcutType.GRID2D_CHANGE_CELL_SIZE_WITH_SCROLL]}>
        <div
          ref={ref_container}
          className={`grid_2d bg-component flex flex-col size-full 
            max-h-full
            max-w-full
          relative cursor-default`}
        >
          <div className="px-2 w-full bg-bar text-sm text-bar-foreground">
            ⓘ This grid contains same {grid2d.total_images_amount} images. Adjust settings to get different images.
          </div>
          <div
            ref={ref_grid}
            className="
            relative
            overflow-auto 

            size-full
            max-h-full
            max-w-full
            
            touch-none select-none
            "
          >
            {render_cell()}
          </div>
          <GRID2D_BARS_bottom entity={generation_current} div_grid={ref_grid.current} {...{ grid2d_virtual, grid2d }} />
        </div>
      </ViewShortcutTrigger>
    </>
  );
}
